import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { formatCad, type AppointmentView } from "@shared/modules/x-atelier";
import { Icon } from "../../../core/components/ui/icon";
import { formatDayShort, formatHour } from "../lib/dates";
import { dueCents, isInactive } from "../lib/status";
import { StatusBadge } from "./status-badge";
import { BookingChannelBadge, NewClientBadge, PresenceBadge } from "./booking-badges";

interface JobRowProps {
  appointment: AppointmentView;
  /** Masqué dans l'historique d'un client (le nom est déjà en titre de page). */
  showClient?: boolean;
}

/**
 * Rangée de travail : une cible tactile pleine largeur, la couleur du
 * technicien en bande, le véhicule en gros, le client, la date, le solde et
 * le statut. Remplace les tableaux dans les listes que les techniciens
 * parcourent au doigt.
 */
export function JobRow({ appointment: a, showClient = true }: JobRowProps) {
  const due = dueCents(a);
  const style = a.technicianColor
    ? ({ "--tech-color": a.technicianColor } as CSSProperties)
    : undefined;
  const classes = ["job-row", isInactive(a.status) && "job-row--inactive"]
    .filter(Boolean)
    .join(" ");
  const sub = [showClient ? a.clientName : null, a.technicianName ?? "Non attribué"]
    .filter(Boolean)
    .join(" · ");
  return (
    <Link to={`/travaux/${a.id}`} className={classes} style={style}>
      <span className="job-row__when">
        <span className="job-row__date">
          {a.schedulePending ? "À planifier" : formatDayShort(a.startAt)}
        </span>
        {!a.schedulePending && <span className="job-row__time">{formatHour(a.startAt)}</span>}
      </span>
      <span className="job-row__main">
        <span className="job-row__title">{a.title}</span>
        <span className="job-row__sub">
          {sub}
          <BookingChannelBadge channel={a.bookingChannel} compact />
          <PresenceBadge at={a.presenceConfirmedAt} compact />
          <NewClientBadge status={a.clientStatus} compact />
        </span>
      </span>
      <span className={`job-row__amount${due === 0 && a.totalWithTaxCents > 0 ? " is-paid" : ""}`}>
        {a.status === "devis" ? (
          formatCad(a.totalWithTaxCents)
        ) : due > 0 ? (
          <>
            {formatCad(due)} <small>dû</small>
          </>
        ) : a.totalWithTaxCents > 0 ? (
          "Payé"
        ) : (
          "—"
        )}
      </span>
      <span className="job-row__status">
        <StatusBadge status={a.status} />
      </span>
      <span className="job-row__chevron" aria-hidden="true">
        <Icon name="chevron-right" size={18} />
      </span>
    </Link>
  );
}
